import React, { useState, useContext, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Table, Badge, Modal } from 'react-bootstrap';
import ProjectContext from '../../context/ProjectContext';
import axios from 'axios';
import EditProjectModal from '../projects/EditProjectModal';

const OrgProjectTeam = ({ org, users }) => {
  const { projects, fetchProjects } = useContext(ProjectContext);
  const [orgProjects, setOrgProjects] = useState([]);
  const [showCreateProject, setShowCreateProject] = useState(false);
  
  // Project creation form state
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: ''
  });

  // Member assignment form state
  const [assignForm, setAssignForm] = useState({
    projectId: '',
    userId: '',
    role: 'viewer'
  });

  // Project editing state
  const [showEditProject, setShowEditProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Filter projects belonging to this organization
  useEffect(() => {
    if (projects && org) {
      setOrgProjects(projects.filter(p => p.organizationId === org._id));
    }
  }, [projects, org]);

  const onProjectChange = e => setProjectForm({ ...projectForm, [e.target.name]: e.target.value });
  const onAssignChange = e => setAssignForm({ ...assignForm, [e.target.name]: e.target.value });

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/projects', {
        ...projectForm,
        organizationId: org._id
      });
      alert('Project created successfully');
      setProjectForm({ name: '', description: '' });
      setShowCreateProject(false);
      if (fetchProjects) fetchProjects();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.msg || 'Error creating project');
    }
  };

  const handleAssignMember = async (e) => {
    e.preventDefault();
    if (!assignForm.projectId || !assignForm.userId) {
      return alert('Please select a project and a user');
    }
    try {
      await axios.post(`/api/projects/${assignForm.projectId}/members`, {
        userId: assignForm.userId,
        role: assignForm.role
      });
      alert('User assigned successfully');
      setAssignForm({ projectId: '', userId: '', role: 'viewer' });
      if (fetchProjects) fetchProjects();
    } catch (err) {
      console.error(err);
      alert('Failed to assign user to project');
    }
  };

  const handleOpenEdit = (project) => {
    setEditingProject(project);
    setShowEditProject(true);
  };

  const handleEditSubmit = async (formData) => {
    try {
      await axios.put(`/api/projects/${editingProject._id}`, formData);
      alert('Project updated successfully');
      setShowEditProject(false);
      setEditingProject(null);
      if (fetchProjects) fetchProjects();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.msg || 'Error updating project');
    }
  };

  return (
    <div className="org-project-team">
      <Row className="mb-4">
        <Col md={8}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="font-weight-bold text-dark mb-0">Projects & Books</h5>
            <Button variant="primary" size="sm" onClick={() => setShowCreateProject(true)}>
              <i className="fas fa-plus mr-2"></i>New Project
            </Button>
          </div>

          {orgProjects.length === 0 ? (
            <Card className="text-center py-5 border-0 bg-light">
              <Card.Body>
                <i className="fas fa-folder-open fa-3x mb-3 text-muted"></i>
                <h6>No projects set up for this organization yet.</h6>
              </Card.Body>
            </Card>
          ) : (
            <div className="table-responsive shadow-sm rounded border bg-white">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Project Name</th>
                    <th>Permissions & Team Members</th>
                    <th className="text-right" style={{ width: '80px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orgProjects.map(p => (
                    <tr key={p._id}>
                      <td className="py-3 font-weight-bold align-middle">
                        {p.name}
                        {p.description && <small className="text-muted d-block font-weight-normal">{p.description}</small>}
                      </td>
                      <td className="py-3 align-middle">
                        {p.members && p.members.length > 0 ? (
                          p.members.map(m => {
                            const u = users.find(usr => usr._id === m.user);
                            return (
                              <div key={m.user} className="mb-1 d-inline-block mr-2">
                                <Badge variant="light" className="border px-2 py-1 mr-1">
                                  {u ? u.name : 'Unknown User'}
                                </Badge>
                                <Badge variant={m.role === 'editor' ? 'info' : 'secondary'}>
                                  {m.role.toUpperCase()}
                                </Badge>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-muted small">No members assigned</span>
                        )}
                      </td>
                      <td className="py-3 align-middle text-right">
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="text-primary p-0" 
                          onClick={() => handleOpenEdit(p)}
                          title="Edit Project Details"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Col>

        <Col md={4}>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Card.Title className="h6 font-weight-bold text-muted small text-uppercase mb-3">Assign Project Member</Card.Title>
              <Form onSubmit={handleAssignMember}>
                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Select Project</Form.Label>
                  <Form.Control
                    as="select"
                    name="projectId"
                    value={assignForm.projectId}
                    onChange={onAssignChange}
                    size="sm"
                    required
                  >
                    <option value="">-- Choose Project --</option>
                    {orgProjects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                  </Form.Control>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Select User</Form.Label>
                  <Form.Control
                    as="select"
                    name="userId"
                    value={assignForm.userId}
                    onChange={onAssignChange}
                    size="sm"
                    required
                  >
                    <option value="">-- Choose User --</option>
                    {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.username || u.phone})</option>)}
                  </Form.Control>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="small font-weight-bold">Role Permission</Form.Label>
                  <Form.Control
                    as="select"
                    name="role"
                    value={assignForm.role}
                    onChange={onAssignChange}
                    size="sm"
                  >
                    <option value="viewer">Viewer (Read-only)</option>
                    <option value="editor">Editor (Write / Edit)</option>
                  </Form.Control>
                </Form.Group>

                <Button type="submit" variant="primary" size="sm" block>
                  Assign to Project
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showCreateProject} onHide={() => setShowCreateProject(false)} centered>
        <Modal.Header closeButton className="py-2 px-3 bg-light">
          <Modal.Title className="h6 mb-0">Create New Project</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Form onSubmit={handleCreateProject}>
            <Form.Group className="mb-3">
              <Form.Label className="small font-weight-bold">Project Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={projectForm.name}
                onChange={onProjectChange}
                size="sm"
                required
                placeholder="e.g. Q2 Real Estate Books"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="small font-weight-bold">Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={projectForm.description}
                onChange={onProjectChange}
                size="sm"
                rows={3}
                placeholder="Brief description of project books..."
              />
            </Form.Group>

            <Button type="submit" variant="primary" size="sm" block>
              Create Project
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <EditProjectModal 
        show={showEditProject}
        onHide={() => {
          setShowEditProject(false);
          setEditingProject(null);
        }}
        onSubmit={handleEditSubmit}
        project={editingProject}
        organizations={[org]}
      />
    </div>
  );
};

export default OrgProjectTeam;
